import { logger } from 'lib/logger';
import { DocumentsApiCall, IDocumentsApiArgs } from 'reactApp/api/DocumentsApiCall';
import {
    allDocumentsLoadError,
    allDocumentsLoadRequest,
    allDocumentsLoadSuccess,
    allDocumentsMoreRequest,
} from 'reactApp/modules/allDocuments/allDocuments.module';
import {
    getAllDocumentsCurrentType,
    getAllDocumentsCursor,
    getExtensionsForType,
} from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { EAllDocumentsType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { normalizeBaseFeedItem } from 'reactApp/modules/feed/feed.helpers';
import { FeedApiResponse } from 'reactApp/modules/feed/feed.types';
import { addHomeItemsToStore } from 'reactApp/modules/home/home.actions';
import { HomeFile } from 'reactApp/modules/home/home.types';
import { makeNewDocumentSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { getLoadMoreLimit } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { put, takeEvery } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';

const LOAD_LIMIT = getLoadMoreLimit(EStorageType.alldocuments);

const getAllDocumentsList = async ({ limit = LOAD_LIMIT, cursor = '', exts = '' }: IDocumentsApiArgs) =>
    (await new DocumentsApiCall().makeRequest({ limit, cursor, exts }))?.data?.body;

function* handleLoadAllDocuments(action: ReturnType<typeof allDocumentsLoadRequest>) {
    const currentDocType = yield* select(getAllDocumentsCurrentType);
    const documentType = action.payload || currentDocType || EAllDocumentsType.document;
    const extensions = yield* select(getExtensionsForType, documentType);

    if (extensions.length === 0) {
        return;
    }

    try {
        const data = yield* call(getAllDocumentsList, { exts: extensions.join(',') });

        yield* call(handleLoadListApiResponse, documentType, data);
    } catch (error: any) {
        logger.error(error);
        yield put(allDocumentsLoadError({ type: documentType, error: error.toStrig() }));
    }
}

function* handleLoadMoreAllDocuments() {
    const documentType = yield* select(getAllDocumentsCurrentType);
    const extensions = yield* select(getExtensionsForType, documentType);
    const cursor = yield* select(getAllDocumentsCursor);

    if (extensions.length === 0) {
        return;
    }

    try {
        const data = yield* call(getAllDocumentsList, { exts: extensions.join(','), cursor });

        yield* call(handleLoadListApiResponse, documentType || EAllDocumentsType.document, data);
    } catch (error: any) {
        logger.error(error);
        yield put(allDocumentsLoadError({ type: documentType, error: error.toStrig() }));
    }
}

function* handleLoadListApiResponse(documentType: EAllDocumentsType, data: FeedApiResponse) {
    const items: HomeFile[] = [];
    const indexes: string[] = [];
    if (data?.objects) {
        data.objects.forEach((apiTem) => {
            const item = {
                ...normalizeBaseFeedItem(apiTem),
                storage: EStorageType.home,
                isMounted: false,
            };
            items.push(item as HomeFile);
            indexes.push(item.id);
        });

        yield put(addHomeItemsToStore(items));
    }

    yield put(allDocumentsLoadSuccess({ type: documentType, data, items: indexes }));
}

export function* watchAllDocuments() {
    yield takeEvery(allDocumentsLoadRequest.toString(), handleLoadAllDocuments);
    yield takeEvery(allDocumentsMoreRequest.toString(), handleLoadMoreAllDocuments);
    yield takeEvery(makeNewDocumentSuccess.toString(), handleLoadAllDocuments);
}
